import React, { useEffect, useState } from "react";
import { UseFormRegister, UseFormSetValue, FieldErrors } from "react-hook-form";
import { Upload, File, Download, Eye, Trash2, StickyNote, Paperclip } from "lucide-react";
import Button from "../UI/Button";
import { FormField, Textarea } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import {
  CreateShipmentData,
  DriverAttachment,
  Shipment,
  SignedDocument,
} from "../../types";
import { PreviewableExtensions } from "../../constants/formOptions";
import { useTranslation } from "react-i18next";
import { shipmentAPI } from "../../services/api";
import { SignedDocumentsSection } from "../shipments/SignedDocumentsSection";

interface AttachmentsSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  setValue: UseFormSetValue<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  newAttachments: File[];
  setNewAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  shipment?: Shipment;
  isEditMode: boolean;
  dispatch: any;
  downloadShipmentAttachment: any;
  toggleAttachmentPublic: any;
}

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  register,
  setValue,
  errors,
  readonly,
  getErrorMessage,
  newAttachments,
  setNewAttachments,
  shipment,
  isEditMode,
  dispatch,
  downloadShipmentAttachment,
}) => {
  const { t } = useTranslation("shipments");

  // Signed Documents — permanent/immutable. Track locally so newly-uploaded
  // docs appear immediately without a full shipment refetch.
  const [signedDocs, setSignedDocs] = useState<SignedDocument[]>(
    shipment?.signedDocuments ?? [],
  );
  useEffect(() => {
    setSignedDocs(shipment?.signedDocuments ?? []);
  }, [shipment?._id, shipment?.signedDocuments]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    setNewAttachments((prevFiles) => {
      const updatedFiles = [...prevFiles, ...selectedFiles];
      setValue("attachments", updatedFiles, { shouldValidate: true });
      return updatedFiles;
    });
    event.target.value = "";
  };

  const removeNewAttachment = (index: number) => {
    setNewAttachments((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== index);
      setValue("attachments", updatedFiles, { shouldValidate: true });
      return updatedFiles;
    });
  };

  const handleDownloadShipmentAttachment = async (
    attachmentId: string,
    previewOnly = false,
  ) => {
    if (!shipment?._id || !attachmentId) return;

    try {
      await dispatch(
        downloadShipmentAttachment({
          shipmentId: shipment._id,
          attachmentId,
          previewOnly,
        }),
      ).unwrap();
    } catch (err: any) {
      console.error("Download error:", err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const downloadAttachment = async (attachment: any) => {
    try {
      const response = await fetch(attachment.fileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = attachment.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {}
  };

  const openAttachment = (attachment: DriverAttachment) => {
    window.open(attachment.fileUrl, "_blank");
  };

  const renderDriverAttachmentRow = (attachment: DriverAttachment) => {
    const ext = attachment.originalName.split(".").pop()?.toLowerCase();
    const isPreviewable = PreviewableExtensions.includes(ext || "");
    return renderFileRow(
      attachment._id,
      attachment.originalName,
      attachment.size,
      <>
        {isPreviewable && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => openAttachment(attachment)}
            className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
            title={t("attachments.view")}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          onClick={() => downloadAttachment(attachment)}
          className="p-1 text-gray-500 hover:text-green-600 transition-colors"
          title={t("attachments.download")}
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
      </>,
    );
  };

  const pohAttachments =
    shipment?.driverAttachments?.filter(
      (attachment) => attachment.attachmentType === "poh",
    ) ?? [];
  const regularDriverAttachments =
    shipment?.driverAttachments?.filter(
      (attachment) => attachment.attachmentType !== "poh",
    ) ?? [];

  const renderFileRow = (
    key: string,
    name: string,
    size: number,
    actions: React.ReactNode,
    extra?: string,
  ) => (
    <div
      key={key}
      className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-200 gap-3"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <File className="h-4 w-4 text-gray-500 shrink-0" />
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-gray-900 truncate">
            {name}
          </p>
          <p className="text-[11px] text-gray-500">
            {formatFileSize(size)}
            {extra ? ` • ${extra}` : ""}
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-1">{actions}</div>
    </div>
  );

  return (
    <>
      <SectionCard icon={StickyNote} title={t("attachments.notesTitle")}>
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.remarks")}
          error={getErrorMessage(errors.additionalNotes)}
        >
          <Textarea
            {...register("additionalNotes")}
            error={!!errors.additionalNotes}
            placeholder={t("attachments.notesPlaceholder")}
            readOnly={readonly}
            rows={3}
            size="sm"
          />
        </FormField>
      </SectionCard>

      <SectionCard icon={Paperclip} title={t("attachments.filesTitle")}>
        {!readonly && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.files")}
            error={getErrorMessage(errors.attachments)}
          >
            <input
              id="attachments"
              type="file"
              multiple
              onChange={handleFileChange}
              className={`block w-full text-[12px] text-gray-500
                file:mr-3 file:py-1.5 file:px-3
                file:rounded-md file:border-0
                file:text-[12px] file:font-semibold
                file:bg-primary-50 file:text-primary-700
                hover:file:bg-primary-100
                ${errors.attachments ? "border-red-500" : ""}`}
              disabled={readonly}
            />
          </FormField>
        )}

        {(newAttachments.length > 0 ||
          (shipment?.attachments && shipment.attachments.length > 0) ||
          (shipment?.driverAttachments &&
            shipment.driverAttachments.length > 0) ||
          (shipment?.publicAttachments &&
            shipment.publicAttachments.length > 0)) && (
          <div className="mt-3 space-y-3">
            {newAttachments.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
                  {t("attachments.newFiles")}
                </p>
                <div className="flex flex-col gap-1.5">
                  {newAttachments.map((file, index) =>
                    renderFileRow(
                      file.name + index,
                      file.name,
                      file.size,
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeNewAttachment(index)}
                        className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                        title={t("attachments.remove")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>,
                    ),
                  )}
                </div>
              </div>
            )}

            {isEditMode &&
              shipment?.attachments &&
              shipment.attachments.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
                    {t("attachments.existingFiles")}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {shipment.attachments.map((attachment) => {
                      const ext = attachment.originalName
                        .split(".")
                        .pop()
                        ?.toLowerCase();
                      const isPreviewable = PreviewableExtensions.includes(
                        ext || "",
                      );
                      return renderFileRow(
                        attachment._id,
                        attachment.originalName,
                        attachment.size,
                        <>
                          {isPreviewable && (
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() =>
                                handleDownloadShipmentAttachment(
                                  attachment._id,
                                  true,
                                )
                              }
                              className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                              title={t("attachments.view")}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() =>
                              handleDownloadShipmentAttachment(
                                attachment._id,
                                false,
                              )
                            }
                            className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                            title={t("attachments.download")}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </>,
                      );
                    })}
                  </div>
                </div>
              )}

            {isEditMode && pohAttachments.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
                  {t("attachments.pohConfirmation")}
                </p>
                <div className="flex flex-col gap-1.5">
                  {pohAttachments.map(renderDriverAttachmentRow)}
                </div>
              </div>
            )}

            {isEditMode && regularDriverAttachments.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
                  {t("attachments.driverFiles")}
                </p>
                <div className="flex flex-col gap-1.5">
                  {regularDriverAttachments.map(renderDriverAttachmentRow)}
                </div>
              </div>
            )}

            {isEditMode &&
              shipment?.publicAttachments &&
              shipment.publicAttachments.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
                    {t("publicView.clientLabel")}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {shipment.publicAttachments.map((attachment) =>
                      renderFileRow(
                        attachment._id,
                        attachment.originalName,
                        attachment.size,
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() =>
                            window.open(attachment.fileUrl, "_blank")
                          }
                          className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                          title={t("attachments.view")}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>,
                        new Date(attachment.uploadedAt).toLocaleDateString(),
                      ),
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
        {newAttachments.length === 0 &&
          (!shipment?.attachments || shipment.attachments.length === 0) && (
            <div className="text-center py-5 text-gray-500">
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-[12px]">{t("attachments.noAttachments")}</p>
            </div>
          )}
      </SectionCard>

      {/* Signed Documents — only once the shipment exists (needs an id to
          upload against). Permanent + immutable, uploadable by admin/user. */}
      {isEditMode && shipment?._id && (
        <SignedDocumentsSection
          documents={signedDocs}
          readonly={readonly}
          uploadFn={(formData) =>
            shipmentAPI.uploadSignedDocuments(shipment._id, formData)
          }
          viewFn={(documentId, preview) =>
            shipmentAPI.downloadSignedDocument(
              shipment._id,
              documentId,
              preview,
            )
          }
          onUploaded={(newDocs) =>
            setSignedDocs((prev) => [...prev, ...newDocs])
          }
        />
      )}
    </>
  );
};
